import { useEffect, useState, useContext, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Trophy, Sparkles, Lock, Calendar, Flame } from 'lucide-react'
import { toast } from 'sonner'
import { AppLayout } from '../../components/AppLayout'
import { Card, CardContent } from '../../components/ui/card'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../../components/ui/accordion'
import { LeagueBadge } from '../../components/achievements/LeagueBadge'
import { LeagueProgressBar } from '../../components/achievements/LeagueProgressBar'
import { MissionCard } from '../../components/achievements/MissionCard'
import { RankingCard } from '../../components/achievements/RankingCard'
import { RankingLockedCard } from '../../components/achievements/RankingLockedCard'
import { CircularLeagueProgress } from '../../components/achievements/CircularLeagueProgress'
import { LeagueXpBar } from '../../components/achievements/LeagueXpBar'
import { StatPill } from '../../components/achievements/StatPill'
import { UpgradeProModal } from '../../components/UpgradeProModal'
import {
  getLeagueConfig,
  LEAGUE_ORDER
} from '../../components/achievements/league-tokens'
import { getAchievements } from '../../api/achievements'
import { AuthContext } from '../../contexts/auth'

function Achievements() {
  const { t, i18n } = useTranslation('achievements')
  const { user } = useContext(AuthContext)
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [openLeague, setOpenLeague] = useState([])

  const fetchState = async () => {
    try {
      setLoading(true)
      const data = await getAchievements()
      setState(data)
      const sync = data?.sync
      if (sync?.newCompletedMissions?.length > 0) {
        sync.newCompletedMissions.forEach(id => {
          toast.success(
            `🎉 ${t('missionCompleted')}: +${getMissionPoints(id, data)} XP`
          )
        })
      }
      if (sync?.newMonthlyAwards?.length > 0) {
        sync.newMonthlyAwards.forEach(id => {
          toast.success(
            `🌙 ${t('monthlyMissionCompleted')}: +${getMissionPoints(id, data)} XP`
          )
        })
      }
      if (sync?.leveledUp) {
        toast.success(
          `🏆 ${t('leveledUpToast', { league: getLeagueConfig(sync.leveledUp).name })}`
        )
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const currentLeague = state?.liga || user?.liga || 'latao'
  const currentLeagueConfig = getLeagueConfig(currentLeague)

  const missionsByLeague = useMemo(() => {
    if (!state?.missions) return {}
    const grouped = {}
    state.missions.forEach(m => {
      if (!grouped[m.league]) grouped[m.league] = []
      grouped[m.league].push(m)
    })
    return grouped
  }, [state])

  const currentLeagueMissions = missionsByLeague[currentLeague] || []
  const completedCount = (state?.missoesCompletas || []).length
  const totalMissions = state?.missions?.length || 0
  const completedThisLeague = currentLeagueMissions.filter(
    m => m.status === 'completed'
  ).length
  const seasonXp = currentLeagueMissions
    .filter(m => m.status === 'completed')
    .reduce((sum, m) => sum + (m.points || 0), 0)
  const maxLeagueXp = currentLeagueMissions.reduce(
    (sum, m) => sum + (m.points || 0),
    0
  )
  const nextLeagueId =
    LEAGUE_ORDER.find(
      id => getLeagueConfig(id).order === currentLeagueConfig.order + 1
    ) || null
  const daysActive = state?.daysActive ?? 0
  const isPro = user?.plano === 'pro'

  const orderedLeagues = LEAGUE_ORDER

  const recentlyCompleted = useMemo(() => {
    if (!state?.missions || !state.missoesCompletas) return []
    const completedIds = state.missoesCompletas.slice(-3).reverse()
    return completedIds
      .map(id => state.missions.find(m => m.id === id))
      .filter(Boolean)
  }, [state])

  return (
    <AppLayout
      title={t('pageTitle')}
      subtitle={t('pageSubtitle')}
      icon={Trophy}
      headerActions={
        <>
          <StatPill
            icon={Calendar}
            label={t('daysActive')}
            value={daysActive.toLocaleString()}
            tone="orange"
          />
          <StatPill
            icon={Flame}
            label={t('totalXp')}
            value={(state?.pontosTotal ?? 0).toLocaleString()}
            tone="purple"
          />
        </>
      }
    >
      <div className="space-y-6">
        {/* Hero — liga atual com anel de progresso */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Card className="overflow-hidden border-slate-200/60">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
                <div className="flex items-center gap-5">
                  <LeagueBadge liga={currentLeague} size="xl" animate />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      {t('currentLeague')}
                    </p>
                    <h2 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                      {currentLeagueConfig.name}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                      {t('completionProgress', {
                        completed: completedThisLeague,
                        total: currentLeagueMissions.length
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex-1 sm:pl-4">
                  <LeagueXpBar
                    liga={currentLeague}
                    nextLeagueId={nextLeagueId}
                    seasonXp={seasonXp}
                    maxLeagueXp={maxLeagueXp || 1}
                  />
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                <span>
                  {completedCount} / {totalMissions} {t('missionsLabel')}
                </span>
                {state?.sync?.leveledUpLocked &&
                  state?.sync?.unlockAvailableAt && (
                    <span className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-amber-800">
                      <Calendar className="h-3.5 w-3.5" />
                      {t('cooldownNotice', {
                        date: new Date(
                          state.sync.unlockAvailableAt
                        ).toLocaleDateString(i18n.language)
                      })}
                    </span>
                  )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Progress bar das 8 ligas */}
        <Card className="border-slate-200/60">
          <CardContent className="p-5 sm:p-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t('leagueProgress')}
            </p>
            <LeagueProgressBar
              currentLeague={currentLeague}
              selectedLeague={openLeague || currentLeague}
              onLeagueClick={setOpenLeague}
            />
          </CardContent>
        </Card>

        {/* Grid: missoes + sidebar */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Lista de missões — accordion (uma liga aberta por vez) */}
          <div className="lg:col-span-2">
            <Card className="border-slate-200/60">
              <CardContent
                className="px-5 sm:px-6"
                style={{ overflowAnchor: 'none' }}
              >
                <Accordion
                  type="multiple"
                  collapsible
                  value={openLeague}
                  onValueChange={setOpenLeague}
                >
                  {orderedLeagues.map(leagueId => {
                    const cfg = getLeagueConfig(leagueId)
                    const missions = missionsByLeague[leagueId] || []
                    if (missions.length === 0) return null
                    const isCurrent = leagueId === currentLeague
                    const completedHere = missions.filter(
                      m => m.status === 'completed'
                    ).length
                    const fractionHere = missions.length
                      ? completedHere / missions.length
                      : 0

                    return (
                      <AccordionItem
                        key={leagueId}
                        value={leagueId}
                        id={`league-section-${leagueId}`}
                        className="scroll-mt-24 overflow-visible"
                      >
                        <AccordionTrigger className="px-1">
                          <div className="flex flex-1 items-center gap-3">
                            <CircularLeagueProgress
                              liga={leagueId}
                              fraction={fractionHere}
                              size={64}
                              stroke={4}
                            >
                              <LeagueBadge liga={leagueId} size="sm" />
                            </CircularLeagueProgress>
                            <div className="text-left">
                              <h3 className="text-sm font-bold text-slate-900 sm:text-base">
                                {cfg.name}
                                {isCurrent && (
                                  <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-700">
                                    {t('currentLeague')}
                                  </span>
                                )}
                              </h3>
                              <p className="text-xs text-slate-500">
                                {completedHere} / {missions.length} •{' '}
                                {missions[0]?.points} XP {t('perMission')}
                              </p>
                            </div>
                            {cfg.requiresPro && (
                              <span className="ml-auto rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-700">
                                Pro
                              </span>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="overflow-visible">
                          <div className="space-y-2 pt-2">
                            {missions.map((m, idx) => (
                              <MissionCard
                                key={m.id}
                                id={m.id}
                                index={idx}
                                title={t(m.titleKey)}
                                description={t(m.descriptionKey)}
                                points={m.points}
                                status={m.status}
                                requiresPro={m.requiresPro}
                                monthly={m.monthly}
                                monthlyLabel={t('monthlyBadge')}
                                onUpgradeClick={() => setUpgradeOpen(true)}
                              />
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )
                  })}
                </Accordion>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {isPro ? (
              <RankingCard
                position={state?.ranking?.position}
                total={state?.ranking?.total}
                points={state?.pontosTotal}
                league={state?.ranking?.league || currentLeague}
              />
            ) : (
              <RankingLockedCard onUpgradeClick={() => setUpgradeOpen(true)} />
            )}

            {/* Recompensas da próxima liga */}
            {(() => {
              const nextOrder = currentLeagueConfig.order + 1
              const nextLeagueId = LEAGUE_ORDER.find(
                id => getLeagueConfig(id).order === nextOrder
              )
              if (!nextLeagueId) return null
              const nextCfg = getLeagueConfig(nextLeagueId)
              return (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3, ease: 'easeOut' }}
                >
                  <Card className="border-slate-200/60">
                    <CardContent className="p-5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {t('nextLeague')}
                      </p>
                      <div className="mt-3 flex items-center gap-3">
                        <LeagueBadge liga={nextLeagueId} size="md" />
                        <div>
                          <p className="font-bold text-slate-900">
                            {nextCfg.name}
                          </p>
                          {nextCfg.requiresPro && (
                            <p className="mt-0.5 flex items-center gap-1 text-xs text-purple-600">
                              <Lock className="h-3 w-3" /> {t('proRequired')}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )
            })()}

            {/* Conquistas recentes */}
            {recentlyCompleted.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4, ease: 'easeOut' }}
              >
                <Card className="border-slate-200/60">
                  <CardContent className="p-5">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {t('recentAchievements')}
                    </p>
                    <div className="space-y-2">
                      {recentlyCompleted.map(m => (
                        <div
                          key={m.id}
                          className="flex items-center gap-2 rounded-lg bg-emerald-50/60 px-3 py-2 text-sm"
                        >
                          <Sparkles className="h-4 w-4 shrink-0 text-emerald-500" />
                          <span className="flex-1 truncate text-slate-700">
                            {t(m.titleKey)}
                          </span>
                          <span className="text-xs font-bold text-emerald-700">
                            +{m.points}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>

        {loading && !state && (
          <div className="text-center text-sm text-slate-500">
            {t('loading')}
          </div>
        )}
      </div>

      <UpgradeProModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        onSuccess={() => fetchState()}
      />
    </AppLayout>
  )
}

function getMissionPoints(missionId, state) {
  const m = state?.missions?.find(x => x.id === missionId)
  return m?.points ?? 0
}

export default Achievements
